import React, { useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import { useTheme } from "@material-ui/core/styles";
import { useDispatch, useSelector } from "react-redux";
import { useSetState } from "Helpers/Hooks";
import MuiButton from "Components/MuiButton/MuiButton";
import DialogContent from "@material-ui/core/DialogContent";
import Grid from "@material-ui/core/Grid";
import Translator from "Languages/Utility/Translator";
import Input from "Components/Input/Input";
import { DialogActions } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import BackDropLoading from "Components/BackDropLoading/BackDropLoading";
import * as R from "ramda";
import { read } from "Helpers/LocalStorage";
import showNotify from "Helpers/Notify";
import {
  getUnitInfo,
  updateUnitInfo,
} from "Stores/actions/properties/ShopAction";
import ActionFormatter from "Components/Table/ActionFormatter/ActionFormatter";
import * as Icons from "react-bootstrap-icons";

export default function ProjectUnitInfo({ row }) {
  const { direction } = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [formValid, setFormValid] = useState({});
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [checkValid, setCheckValid] = useState(false);
  const [edited, setEdited] = useState(false);
  const { selectedProject } = useSelector((state) => ({
    selectedProject: state.shop.selectedProject,
  }));

  const [controls, setControls] = useSetState({
    nameUnit: {
      type: "text",
      value: "",
      validation: {
        required: true,
      },
      col: {
        sm: 12,
        xs: 12,
      },
    },
    minWstandard: {
      type: "text",
      value: "",
      validation: {
        required: true,
      },
      col: {
        sm: 6,
        xs: 12,
      },
    },
    maxWstandard: {
      type: "text",
      value: "",
      validation: {
        required: true,
      },
      col: {
        sm: 6,
        xs: 12,
      },
    },
    minHstandard: {
      type: "text",
      value: "",
      validation: {
        required: true,
      },
      col: {
        sm: 6,
        xs: 12,
      },
    },
    maxHstandard: {
      type: "text",
      value: "",
      validation: {
        required: true,
      },
      col: {
        sm: 6,
        xs: 12,
      },
    },
    tag: {
      type: "text",
      value: "",
      validation: {
        required: false,
      },
      col: {
        sm: 12,
        xs: 12,
      },
    },
    describe: {
      type: "text",
      value: "",
      validation: {
        required: false,
      },
      col: {
        sm: 12,
        xs: 12,
      },
    },
  });

  const handleDialog = () => setOpenDialog(!openDialog);

  const onEnter = async () => {
    onClear();
  };

  const onClear = async () => {
    setLoading(true);
    const data = await dispatch(
      getUnitInfo({
        KeyGroup: read("groupKey"),
        KeyUser: read("userKey"),
        idUnit: row.Id,
        idProject: selectedProject.IdProject,
      })
    );
    setControls({ nameUnit: { value: data.Name } });
    setControls({ minWstandard: { value: data.MinWidth } });
    setControls({ maxWstandard: { value: data.MaxWidth } });
    setControls({ minHstandard: { value: data.MinHeight } });
    setControls({ maxHstandard: { value: data.MaxHeight } });
    setControls({ tag: { value: data.TagAnimation } });
    setControls({ describe: { value: data.Description } });
    const validation = {
      nameUnit: data.Name !== "",
      minWstandard: +data.MinWidth !== 0,
      maxWstandard: +data.MaxWidth !== 0,
      minHstandard: +data.MinHeight !== 0,
      maxHstandard: +data.MaxHeight !== 0,
    };
    setFormValid(validation);
    setCheckValid(Object.values(validation).every((item) => item === true));
    setEdited(false);
    setLoading(false);
  };

  const onExit = () => {
    setOpenDialog(false);
  };

  const setValue = async (name, value, validation) => {
    setControls({ [name]: { value: value } });
    setFormValid((prev) => ({ ...prev, [name]: validation }));
    setEdited(true);
  };

  const onFinish = async (e) => {
    e.preventDefault();
    if (
      !R.isEmpty(formValid) &&
      Object.values(formValid).every((item) => item === true)
    ) {
      if (edited) {
        const {
          nameUnit,
          minWstandard,
          maxWstandard,
          minHstandard,
          maxHstandard,
          tag,
          describe,
        } = controls;
        try {
          const data = {
            KeyGroup: read("groupKey"),
            KeyUser: read("userKey"),
            idUnit: row.Id,
            name: nameUnit.value,
            minWidth: minWstandard.value,
            maxWidth: maxWstandard.value,
            minHeight: minHstandard.value,
            maxHeight: maxHstandard.value,
            description: describe.value,
            TagAnimation: tag.value,
          };
          setLoading(true);
          await dispatch(updateUnitInfo(data));
          onExit();
          setLoading(false);
        } catch (e) {
          showNotify({
            text: t("correctFormDataRequired"),
            customClass: "error",
          });
        }
      } else {
        onExit();
      }
    } else {
      setCheckValid(true);
    }
  };

  return (
    <>
      <ActionFormatter
        title={<Translator id="unitDetails" />}
        icon={<Icons.Gear size={17} />}
        className="primary-button"
        onClick={handleDialog}
      />
      <Dialog
        dir={direction}
        open={openDialog}
        TransitionProps={{ onEnter: onEnter, onExit: onExit }}
        onClose={handleDialog}
        fullWidth
        maxWidth="sm"
      >
        <div>
          <div className="dialogHeader">
            <p>{`${t("unitDetails")} (${row.Name})`}</p>
          </div>
          <DialogContent id="content-dialog-manager">
            <BackDropLoading loading={loading} />
            <Grid container spacing={2}>
              {Object.keys(controls).map((key, index) => {
                return (
                  <Grid
                    item
                    xs={controls[key].col.xs}
                    sm={controls[key].col.sm}
                    key={key + index}
                  >
                    {controls[key].type === "text" && (
                      <Input
                        setValue={setValue.bind(this)}
                        value={controls[key].value}
                        placeholder={t(key)}
                        name={key}
                        validation={controls[key].validation}
                        label={<Translator id={key} />}
                        type="text"
                        checkValid={checkValid && !formValid[key]}
                      />
                    )}
                  </Grid>
                );
              })}
            </Grid>
          </DialogContent>
          <DialogActions>
            <MuiButton
              loading={loading}
              color="primary"
              title={<Translator id="button_save" />}
              onClick={onFinish}
            />
            <MuiButton
              onClick={handleDialog}
              title={<Translator id="enseraf" />}
            />
          </DialogActions>
        </div>
      </Dialog>
    </>
  );
}
